import { createAsyncThunk } from '@reduxjs/toolkit';
import Endpoints from 'constants/endpoints';
import {
  AdminTeamsResponse,
  CreateAdminTeamPayload,
  FetchAdminTeamsParams,
  UpdateAdminTeamPayload
} from 'interfaces/admin-teams.interface';
import { toast } from 'react-toastify';
import focusBearApi from 'services/axios-config';
import { t } from 'services/i18n.service';
import { captureException } from 'services/posthog.service';
import { getApiErrorMessage } from 'store/reducer/admin/extra';

/**
 * Focus Bear staff team list (GET team-management/admin/teams — plan G1).
 * Named to avoid clashing with `fetchAdminTeams` in store/reducer/team/extra.ts
 * (the teams the *user* administers).
 * Loading flags live in the slice's pending/settled extraReducers — dispatching
 * slice actions from here would recreate the extra↔slice import cycle that
 * breaks the webpack build (TDZ on `fetchAdminTeamsList`).
 */
export const fetchAdminTeamsList = createAsyncThunk(
  'admin_teams/fetch_admin_teams_list',
  async (params: FetchAdminTeamsParams) => {
    try {
      const { data } = await focusBearApi.get<AdminTeamsResponse>(
        Endpoints.ADMIN_TEAMS,
        { params }
      );
      return data ?? { teams: [], total_count: 0 };
    } catch (error) {
      captureException(error);
      throw error;
    }
  }
);

export const createAdminTeam = createAsyncThunk(
  'admin_teams/create_admin_team',
  async (payload: CreateAdminTeamPayload) => {
    try {
      const { data } = await focusBearApi.post(Endpoints.ADMIN_TEAMS, payload);
      toast.success(t('admin_teams.team_created'));
      return data ?? null;
    } catch (error) {
      // Backend messages (owner not found, validation) surface verbatim.
      toast.error(
        getApiErrorMessage(error, t('admin_teams.team_create_failed'))
      );
      captureException(error);
      throw error;
    }
  }
);

export const updateAdminTeam = createAsyncThunk(
  'admin_teams/update_admin_team',
  async (payload: UpdateAdminTeamPayload) => {
    try {
      const { id, ...body } = payload;
      const { data } = await focusBearApi.patch(
        Endpoints.ADMIN_TEAM_BY_ID(id),
        body
      );
      toast.success(t('admin_teams.team_updated'));
      return data ?? null;
    } catch (error) {
      // 409/400 on payment-type changes for teams with live Stripe data must
      // reach the admin verbatim (plan G1 guardrail).
      toast.error(
        getApiErrorMessage(error, t('admin_teams.team_update_failed'))
      );
      captureException(error);
      throw error;
    }
  }
);
